import { useAuthenticator } from '@aws-amplify/ui-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GlobalContext } from 'contexts/GlobalContext';
import {
	IConfigsCompany,
	IModulesPermissions,
	IModulesPermissionsUser,
	IPermissionsPage,
	IPermissionsPagesUser,
} from 'contexts/models/globalContextModels';
import { useEffect, useState } from 'react';
import getRoutesAdminCompany from 'routes/routesAdmin';
import api from 'services/api/api';
import { getPermissions, login } from 'services/api/requests/authentication';

import alb from '../../services/api/alb';
import apiPython from '../../services/api/apiPython';
import statisticsApi from '../../services/api/statisticsApi';
interface IGlobalProviderProps {
	children: JSX.Element | JSX.Element[];
}

function GlobalProvider({ children }: IGlobalProviderProps) {
	const [pathCompanyName, setPathCompanyName] = useState<string | undefined>(
		''
	);
	const [userAttributes, setUserAttributes] = useState<any>(null);
	const [userInfos, setUserInfos] = useState<any>(null);
	const [configsCompany, setConfigsCompany] = useState<IConfigsCompany>({
		precisao_decimal: 0,
	});
	const [permissionsPage, setPermissionsPage] =
		useState<IPermissionsPagesUser>({} as IPermissionsPagesUser);
	const [modulesPermissions, setModulesPermissions] =
		useState<IModulesPermissionsUser>({});

	const { user, route } = useAuthenticator(context => [
		context.user,
		context.route,
	]);
	const queryClient = useQueryClient();

	// Função para criar o objeto de permissões
	const createPermissionsPageObject = (
		data: IPermissionsPage[]
	): IPermissionsPagesUser => {
		return data.reduce((obj, page) => {
			const pagina = page.recurso;
			obj[pagina] = {
				visualizar: page.visualizar,
				cadastrar: page.cadastrar,
				editar: page.editar,
				deletar: page.deletar,
			};
			return obj;
		}, {} as IPermissionsPagesUser);
	};

	// Função para criar o objeto de permissões dos módulos
	const createPermissionsModuleObject = (
		data: IModulesPermissions[]
	): IModulesPermissionsUser => {
		return data.reduce((obj, module) => {
			const nameModule = module.recurso;
			const permissions = module.acesso === 'true';
			obj[nameModule] = {
				recurso: nameModule,
				permissions,
				type: module.tipo,
			};
			return obj;
		}, {} as IModulesPermissionsUser);
	};

	const { refetch: easyPermissions } = useQuery(
		['permissions'],
		() =>
			getPermissions(
				user.getSignInUserSession()?.getRefreshToken()?.getToken()
			),
		{
			enabled: false,
			refetchOnWindowFocus: false,
		}
	);
	const authorizationAndPermissionsAssigns = async () => {
		if (user) {
			const session = user.getSignInUserSession();
			const getToken = session?.getIdToken();
			const token: any = getToken?.getJwtToken();
			api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
			alb.defaults.headers.common['Authorization'] = `Bearer ${token}`;
			statisticsApi.defaults.headers.common[
				'Authorization'
			] = `Bearer ${token}`;

			apiPython.defaults.headers.common[
				'Authorization'
			] = `Bearer ${token}`;
			const userAttributes = user.attributes;
			const removeSpacesAndSpecialCharacters = text => {
				return text
					.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, '')
					.toLowerCase()
					.trim()
					.replace(/[\.,]/g, '')
					.replace(/[\(\)]/g, '')
					.replace(/["']/g, '')
					.replace(/\s/g, '');
			};
			const fantasyName = userAttributes?.['custom:nomeFantasia'] || '';
			const companyName = removeSpacesAndSpecialCharacters(fantasyName);
			if (!companyName) {
				setPathCompanyName('timeeasy360');
			} else {
				setPathCompanyName(companyName);
			}

			setUserAttributes({ ...userAttributes, token });

			const permissions = await easyPermissions();

			if (permissions) {
				const {
					restricoesRecursoUsuario,
					restricaoRecursoPlanoServices,
					usuario,
					empresa,
				} = permissions.data;

				if (restricoesRecursoUsuario) {
					const permissionsPageObject = createPermissionsPageObject(
						restricoesRecursoUsuario
					);
					setPermissionsPage(permissionsPageObject);
				}
				if (restricaoRecursoPlanoServices) {
					const modulesPermissionObject =
						createPermissionsModuleObject(
							restricaoRecursoPlanoServices
						);
					setModulesPermissions(modulesPermissionObject);
				}
				setUserInfos(usuario);
				setConfigsCompany(empresa);
			}
			queryClient.removeQueries(['cycle-list']);
		}
	};

	useEffect(() => {
		authorizationAndPermissionsAssigns();

		if (user && !sessionStorage.getItem('loginLogged')) {
			try {
				login({});
				sessionStorage.setItem('loginLogged', 'true');
			} catch (error) {
				console.error('Erro ao registrar login:', error);
			}
		}
	}, [user]);

	useEffect(() => {
		if (route === 'setup' && !user) {
			setPathCompanyName('easy360');
		}
	}, [route]);

	return (
		<GlobalContext.Provider
			value={{
				routes: getRoutesAdminCompany(
					pathCompanyName as string,
					permissionsPage,
					modulesPermissions,
					userAttributes
				),
				pathCompanyName,
				userAttributes,
				permissionsPage,
				modulesPermissions,
				setUserAttributes,
				userInfos,
				setUserInfos,
				configsCompany,
				refreshPermissions: authorizationAndPermissionsAssigns,
			}}
		>
			{children}
		</GlobalContext.Provider>
	);
}

export default GlobalProvider;
